import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { useToast } from "../components/ui/toast";

type Step = 1 | 2 | 3;

export function FunnelCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [step, setStep] = useState<Step>(1);
  const [pipelineId, setPipelineId] = useState("");
  const [pipelineName, setPipelineName] = useState("");
  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>([]);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [channelId, setChannelId] = useState("");

  const pipelines = useQuery({
    queryKey: ["pipelines"],
    queryFn: api.listPipelines,
  });
  const channels = useQuery({
    queryKey: ["wazzup-channels"],
    queryFn: api.listWazzupChannels,
  });
  const statuses = useQuery({
    queryKey: ["pipeline-statuses", pipelineId],
    queryFn: () => api.listPipelineStatuses(pipelineId),
    enabled: pipelineId.length > 0,
  });

  const create = useMutation({
    mutationFn: async () => {
      const funnel = await api.createFunnel({
        amoPipelineId: pipelineId,
        amoStatusIds: selectedStatusIds,
        name: pipelineName,
        systemPrompt,
      });
      if (channelId) {
        const ch = channels.data?.find((c) => c.id === channelId);
        if (ch) {
          await api.bindChannel(funnel.id, {
            wazzupChannelId: ch.id,
            type: ch.type,
          });
        }
      }
      return funnel;
    },
    onSuccess: (funnel) => {
      queryClient.invalidateQueries({ queryKey: ["funnels"] });
      queryClient.invalidateQueries({ queryKey: ["account"] });
      toast("Агент создан", "success");
      navigate(`/funnels/${funnel.id}`);
    },
    onError: (err) => {
      const message =
        err instanceof ApiError ? err.message : "Не удалось создать агента";
      toast(message, "destructive");
    },
  });

  return (
    <div className="mx-auto max-w-xl space-y-4 p-4">
      <Link to="/settings" className="text-sm text-muted-foreground">
        ← Отмена
      </Link>
      <div className="text-sm text-muted-foreground">Шаг {step} из 3</div>

      <Card>
        <CardHeader>
          <CardTitle>
            {step === 1 && "Выберите воронку amoCRM"}
            {step === 2 && "Системный промпт"}
            {step === 3 && "Подключите канал Wazzup"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {step === 1 && (
            <>
              <Label htmlFor="pipeline">Воронка amoCRM</Label>
              <Select
                id="pipeline"
                value={pipelineId}
                onChange={(e) => {
                  const id = e.target.value;
                  setPipelineId(id);
                  setSelectedStatusIds([]);
                  setPipelineName(
                    pipelines.data?.find((p) => p.id === id)?.name ?? "",
                  );
                }}
              >
                <option value="">— выберите —</option>
                {pipelines.data?.map((p) => (
                  <option key={p.id} value={p.id} disabled={p.bound}>
                    {p.name}
                    {p.bound ? " (уже подключена)" : ""}
                  </option>
                ))}
              </Select>

              {pipelineId && (
                <div className="space-y-2">
                  <div>
                    <Label>Этапы, где агент отвечает</Label>
                    <p className="text-xs text-muted-foreground">
                      Если ничего не выбрать, агент будет отвечать на всех этапах.
                    </p>
                  </div>
                  {statuses.isLoading && (
                    <div className="text-sm text-muted-foreground">
                      Загружаем этапы...
                    </div>
                  )}
                  {statuses.error && (
                    <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                      Не удалось загрузить этапы. Можно продолжить и настроить их позже.
                    </div>
                  )}
                  {statuses.data && statuses.data.length > 0 && (
                    <div className="space-y-2 rounded border p-3">
                      {statuses.data.map((status) => (
                        <label
                          key={status.id}
                          className="flex items-center gap-2 text-sm"
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={selectedStatusIds.includes(status.id)}
                            onChange={(e) => {
                              setSelectedStatusIds((current) =>
                                e.target.checked
                                  ? [...current, status.id]
                                  : current.filter((id) => id !== status.id),
                              );
                            }}
                          />
                          <span
                            className="h-3 w-3 rounded-full border"
                            style={{ backgroundColor: status.color ?? "transparent" }}
                          />
                          <span>{status.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {statuses.data && statuses.data.length === 0 && (
                    <div className="text-sm text-muted-foreground">
                      У этой воронки нет доступных этапов.
                    </div>
                  )}
                </div>
              )}
              <Button disabled={!pipelineId} onClick={() => setStep(2)}>
                Далее →
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              <Label htmlFor="prompt">Опишите, как агент должен общаться</Label>
              <Textarea
                id="prompt"
                rows={8}
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="Роль, тон, что предлагать, когда передать менеджеру"
              />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)}>
                  ← Назад
                </Button>
                <Button
                  disabled={systemPrompt.trim().length === 0}
                  onClick={() => setStep(3)}
                >
                  Далее →
                </Button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              {channels.data && channels.data.length > 0 ? (
                <>
                  <Label htmlFor="channel">Канал Wazzup</Label>
                  <Select
                    id="channel"
                    value={channelId}
                    onChange={(e) => setChannelId(e.target.value)}
                  >
                    <option value="">— без канала (подключить позже) —</option>
                    {channels.data.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} · {c.type}
                      </option>
                    ))}
                  </Select>
                </>
              ) : (
                <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm">
                  Сначала подключите Wazzup, чтобы выбрать канал.{" "}
                  <Link to="/settings/wazzup" className="underline">
                    Подключить
                  </Link>
                  <div className="mt-1 text-muted-foreground">
                    Можно создать агента и подключить канал позже.
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(2)}>
                  ← Назад
                </Button>
                <Button
                  disabled={create.isPending}
                  onClick={() => create.mutate()}
                >
                  {create.isPending ? "Создаём..." : "Создать агента"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
